import { z } from 'zod'
import { OUTREACH_DISMISS_REASONS, OUTREACH_REPLY_CLASSES } from './status'

/**
 * One opportunity, judged and — where it survives — written.
 *
 * One call rather than a qualification, an angle and a draft each seeing a
 * slice: only the step that wrote the mail saw the page, the offer and the site
 * together, so it vetoed most of what the other two had paid to carry to it.
 *
 * Nested because the gateway hands the keys back in alphabetical order, and a
 * flat object had the model write `body` before it had decided anything —
 * measured, three drafts in five came back empty. `assessment` sorts before
 * `email`, and inside it `verdict` sorts after the hook, the reason and the
 * target, so the decision is written after the working rather than before it.
 *
 * `targetUrl` must be one of the urls it was shown; the hook refuses anything
 * else. Strict mode needs every property present, so a dismissal answers the
 * mail empty.
 */
export const outreachJudgementSchema = z.object({
  assessment: z.object({
    dismissReason: z.enum(OUTREACH_DISMISS_REASONS).nullable(),
    hook: z.string().max(400).nullable(),
    reason: z.string().max(500),
    recipientName: z.string().max(80).nullable(),
    targetUrl: z.string().nullable(),
    verdict: z.enum(['write', 'dismiss']),
  }),
  email: z.object({
    subject: z.string().max(200),
    body: z.string().max(4000),
  }),
})

export type OutreachJudgement = z.infer<typeof outreachJudgementSchema>

/** The bump. Its own shape because it has nothing to decide but whether to
 * send it at all. */
export const outreachFollowUpSchema = z.object({
  skip: z.boolean(),
  body: z.string().max(2000),
})

export type OutreachFollowUp = z.infer<typeof outreachFollowUpSchema>

/**
 * What one inbound message is, when the headers could not say. `reason` makes a
 * misclassification arguable afterwards: an unsubscribe suppresses an address
 * for the whole workspace forever.
 */
export const outreachReplyClassificationSchema = z.object({
  classification: z.enum(OUTREACH_REPLY_CLASSES),
  reason: z.string().min(4).max(400),
})

export type OutreachReplyClassification = z.infer<typeof outreachReplyClassificationSchema>

export const OUTREACH_JUDGEMENT_SCHEMA_NAME = 'outreach_judgement'
export const OUTREACH_FOLLOW_UP_SCHEMA_NAME = 'outreach_follow_up'
export const OUTREACH_REPLY_CLASSIFICATION_SCHEMA_NAME = 'outreach_reply_classification'

/** Every `ai_prompt` slot this feed runs through. */
export const OUTREACH_PROMPT_SLOTS = {
  judge: 'outreach.judge',
  followUp: 'outreach.follow-up',
  classifyReply: 'outreach.classify-reply',
} as const

export type OutreachPromptSlot = (typeof OUTREACH_PROMPT_SLOTS)[keyof typeof OUTREACH_PROMPT_SLOTS]

/** Where each answer lands. The run carries the name because nothing is on the
 * stack when it arrives. */
export const OUTREACH_JUDGE_HOOK = 'outreach.judge'
export const OUTREACH_FOLLOW_UP_HOOK = 'outreach.follow-up'
export const OUTREACH_CLASSIFY_REPLY_HOOK = 'outreach.classify-reply'

/**
 * Strict mode rejects `$schema`, demands `additionalProperties: false` and
 * requires every property to be listed. Derived from the zod object the answer
 * is parsed against, so the two cannot drift.
 */
function jsonSchema(schema: z.ZodType): Record<string, unknown> {
  const { $schema, ...rest } = z.toJSONSchema(schema, { target: 'draft-7' }) as Record<
    string,
    unknown
  > & { $schema?: string }

  return { ...rest, additionalProperties: false }
}

function responseFormat(name: string, schema: z.ZodType): Record<string, unknown> {
  return {
    response_format: {
      type: 'json_schema',
      json_schema: { name, strict: true, schema: jsonSchema(schema) },
    },
  }
}

export function outreachJudgementResponseFormat(): Record<string, unknown> {
  return responseFormat(OUTREACH_JUDGEMENT_SCHEMA_NAME, outreachJudgementSchema)
}

export function outreachFollowUpResponseFormat(): Record<string, unknown> {
  return responseFormat(OUTREACH_FOLLOW_UP_SCHEMA_NAME, outreachFollowUpSchema)
}

export function outreachReplyClassificationResponseFormat(): Record<string, unknown> {
  return responseFormat(
    OUTREACH_REPLY_CLASSIFICATION_SCHEMA_NAME,
    outreachReplyClassificationSchema,
  )
}
